import actionCreatorFactory from "typescript-fsa";
import { reducerWithInitialState } from "typescript-fsa-reducers";
import { ConfirmDialogProps, DialogProps, DialogType } from "../types/types";
import { RootState } from "./index.store";

/**
 * States
 */
export interface UiState {
  isLoading: boolean;
  showMessageBar: boolean;
  messageBarMessage: string;
  dialogOpened: boolean;
  dialogProps: DialogProps;
  confirmDialogProps: ConfirmDialogProps;
}

export const uiInitialState: UiState = {
  isLoading: false,
  showMessageBar: false,
  messageBarMessage: "",
  dialogOpened: false,
  dialogProps: {
    isOpen: false
  },
  confirmDialogProps: {
    isOpen: false
  }
};

export const uiSelector = (state: RootState) => state.ui;
const s = uiSelector;

export const dialogPropsSelector = (state: RootState) => s(state).dialogProps;
export const confirmDialogPropsSelector = (state: RootState) =>
  s(state).confirmDialogProps;

/**
 * Actions
 */
const actionCreator = actionCreatorFactory("ui");
export const uiActions = {
  /* SYSTEM ACTIONS */
  SYSTEM_LOADING_STARTED: actionCreator("SYSTEM_LOADING_STARTED"),
  SYSTEM_LOADING_STOPPED: actionCreator("SYSTEM_LOADING_STOPPED"),
  SYSTEM_SHOW_MESSAGE_BAR: actionCreator<{ message: string }>(
    "SYSTEM_SHOW_MESSAGE_BAR"
  ),
  SYSTEM_HIDE_MESSAGE_BAR: actionCreator("SYSTEM_HIDE_MESSAGE_BAR"),
  SYSTEM_OPEN_DIALOG: actionCreator<{
    type: DialogType;
    title: string;
    isModal: boolean;
  }>("SYSTEM_OPEN_DIALOG"),
  SYSTEM_CLOSE_DIALOG: actionCreator<{ formId?: string }>(
    "SYSTEM_CLOSE_DIALOG"
  ),
  SYSTEM_OPEN_CONFIRM_DIALOG: actionCreator<{
    title: string;
    subText?: string;
    primaryAction: any;
    secondaryAction?: any;
  }>("SYSTEM_OPEN_CONFIRM_DIALOG"),
  SYSTEM_CLOSE_CONFIRM_DIALOG: actionCreator("SYSTEM_CLOSE_CONFIRM_DIALOG"),

  /* USER ACTIONS */
  USER_CLICKED_CONFIRM_PRIMARY_BUTTON: actionCreator(
    "USER_CLICKED_CONFIRM_PRIMARY_BUTTON"
  ),
  USER_CLICKED_CONFIRM_SECONDARY_BUTTON: actionCreator(
    "USER_CLICKED_CONFIRM_SECONDARY_BUTTON"
  ),
  USER_CLICKED_CLOSE_DIALOG_BUTTON: actionCreator(
    "USER_CLICKED_CLOSE_DIALOG_BUTTON"
  ),

  /* REDUCER ACTIONS */
  REDUCER_LOADING_STARTED: actionCreator("REDUCER_LOADING_STARTED"),
  REDUCER_LOADING_STOPPED: actionCreator("REDUCER_LOADING_STOPPED"),
  REDUCER_SHOW_MESSAGE_BAR: actionCreator<{ message: string }>(
    "REDUCER_SHOW_MESSAGE_BAR"
  ),
  REDUCER_HIDE_MESSAGE_BAR: actionCreator("REDUCER_HIDE_MESSAGE_BAR"),
  REDUCER_SET_DIALOG_PROPS: actionCreator<{ dialogProps: DialogProps }>(
    "REDUCER_SET_DIALOG_PROPS"
  ),
  REDUCER_SET_CONFIRM_DIALOG_PROPS: actionCreator<ConfirmDialogProps>(
    "REDUCER_SET_CONFIRM_DIALOG_PROPS"
  )
};

/**
 * Reducer
 */
export const uiReducer = reducerWithInitialState<UiState>(uiInitialState)
  .case(uiActions.REDUCER_LOADING_STARTED, state => ({
    ...state,
    isLoading: true
  }))
  .case(uiActions.REDUCER_LOADING_STOPPED, state => ({
    ...state,
    isLoading: false
  }))
  .case(uiActions.REDUCER_SHOW_MESSAGE_BAR, (state, payload) => ({
    ...state,
    showMessageBar: true,
    messageBarMessage: payload.message
  }))
  .case(uiActions.REDUCER_HIDE_MESSAGE_BAR, state => ({
    ...state,
    showMessageBar: false
  }))
  .case(uiActions.REDUCER_SET_DIALOG_PROPS, (state, payload) => ({
    ...state,
    dialogProps: payload.dialogProps
  }))
  .case(uiActions.REDUCER_SET_CONFIRM_DIALOG_PROPS, (state, payload) => ({
    ...state,
    confirmDialogProps: payload
  }));
